import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Shield, ShieldCheck, ShieldAlert, Lock, AlertCircle, UserPlus, Mail, User } from "lucide-react";

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;

const roleConfig = {
  admin: { label: "Admin", icon: ShieldCheck, color: "bg-purple-100 text-purple-700" },
  user: { label: "User", icon: Shield, color: "bg-blue-100 text-blue-700" },
  viewer: { label: "Viewer", icon: ShieldAlert, color: "bg-amber-100 text-amber-700" },
};

export default function UsersPage() {
  const { user: currentUser, createUser, isAdmin } = useAuth();
  const [roleDialog, setRoleDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState("user");
  const [createError, setCreateError] = useState("");

  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
    initialData: [],
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role, password }) =>
      base44.entities.User.updateRole(id, role, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setRoleDialog(null);
      setVerifyPassword("");
      setVerifyError("");
      if (window.addNotification) {
        window.addNotification("Role updated successfully", "success");
      }
    },
    onError: (err) => {
      setVerifyError(err.message || "Failed to update role");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id }) => base44.entities.User.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setDeleteDialog(null);
      setVerifyError("");
      if (window.addNotification) {
        window.addNotification("User deleted successfully", "success");
      }
    },
    onError: (err) => {
      setVerifyError(err.message || "Failed to delete user");
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: ({ id, verifyPassword, newPassword }) =>
      base44.entities.User.changePassword(id, verifyPassword, newPassword),
    onSuccess: () => {
      setPasswordDialog(null);
      setVerifyPassword("");
      setNewPassword("");
      setVerifyError("");
      if (window.addNotification) {
        window.addNotification("Password updated successfully", "success");
      }
    },
    onError: (err) => {
      setVerifyError(err.message || "Failed to update password");
    },
  });

  const createUserMutation = useMutation({
    mutationFn: ({ name, email, password, role }) =>
      createUser(name, email, password, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setCreateUserDialog(false);
      setCreateName("");
      setCreateEmail("");
      setCreatePassword("");
      setCreateRole("user");
      setCreateError("");
      if (window.addNotification) {
        window.addNotification("User created successfully", "success");
      }
    },
    onError: (err) => {
      setCreateError(err.message || "Failed to create user");
    },
  });

  const handleCreateUser = (e) => {
    e.preventDefault();
    setCreateError("");
    if (!createName.trim() || !createEmail.trim() || !createPassword) {
      setCreateError("Name, email, and password are required");
      return;
    }
    if (createPassword.length < 8) {
      setCreateError("Password must be at least 8 characters");
      return;
    }
    if (!PASSWORD_REGEX.test(createPassword)) {
      setCreateError("Password must include uppercase, lowercase, number, and special character (@$!%*?&)");
      return;
    }
    createUserMutation.mutate({
      name: createName.trim(),
      email: createEmail.trim(),
      password: createPassword,
      role: createRole,
    });
  };

  const handleUpdateRole = (e) => {
    e.preventDefault();
    if (!roleDialog || !verifyPassword) {
      setVerifyError("Password is required for verification");
      return;
    }
    setVerifyError("");
    updateRoleMutation.mutate({
      id: roleDialog.id,
      role: roleDialog.newRole,
      password: verifyPassword,
    });
  };

  const handleDeleteUser = (e) => {
    e.preventDefault();
    if (!deleteDialog) return;
    setVerifyError("");
    deleteMutation.mutate({ id: deleteDialog.id });
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (!passwordDialog || !verifyPassword || !newPassword) {
      setVerifyError("Your password and the new password are required");
      return;
    }
    if (newPassword.length < 6) {
      setVerifyError("New password must be at least 6 characters");
      return;
    }
    setVerifyError("");
    changePasswordMutation.mutate({
      id: passwordDialog.id,
      verifyPassword,
      newPassword,
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">User Management</h1>
          <p className="text-sm text-slate-600">
            Manage roles and permissions. Sensitive actions require password verification.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setCreateUserDialog(true)}
            className="shrink-0"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto" />
        </div>
      ) : (
        <div className="grid gap-4">
          {users.map((u) => {
            const config = roleConfig[u.role] || roleConfig.user;
            const RoleIcon = config.icon;
            const isCurrentUser = currentUser?.id === u.id;
            const adminCount = users.filter((x) => x.role === "admin").length;
            const isLastAdmin = u.role === "admin" && adminCount <= 1;
            const canDelete = !isCurrentUser && !isLastAdmin;

            return (
              <Card key={u.id} className="shadow-sm border border-slate-200">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center">
                        <span className="text-lg font-bold text-amber-400">
                          {u.name?.charAt(0).toUpperCase() || "?"}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{u.name}</h3>
                        <p className="text-sm text-slate-600">{u.email}</p>
                        <span
                          className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}
                        >
                          <RoleIcon className="w-3 h-3" />
                          {config.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPasswordDialog({ id: u.id, name: u.name })
                        }
                      >
                        Change Password
                      </Button>
                      {!isCurrentUser && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setRoleDialog({
                                id: u.id,
                                name: u.name,
                                currentRole: u.role,
                                newRole: u.role,
                              })
                            }
                          >
                            Change Role
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() =>
                              setDeleteDialog({ id: u.id, name: u.name })
                            }
                            disabled={!canDelete}
                            title={isLastAdmin ? "Cannot delete the last admin" : undefined}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Role change - double verification */}
      <Dialog
        open={!!roleDialog}
        onOpenChange={(open) => {
          if (!open) setRoleDialog(null);
          setVerifyError("");
          setVerifyPassword("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Changing role for <strong>{roleDialog?.name}</strong>.
          </p>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              New Role
            </label>
            <Select
              value={roleDialog?.newRole || ""}
              onValueChange={(v) =>
                setRoleDialog((prev) => (prev ? { ...prev, newRole: v } : null))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <form onSubmit={handleUpdateRole} className="space-y-4">
            {verifyError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {verifyError}
              </div>
            )}
            <div>
              <label htmlFor="role-verify-password" className="block text-sm font-medium text-slate-700 mb-1">
                Your Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="role-verify-password"
                  name="verifyPassword"
                  type="password"
                  value={verifyPassword}
                  onChange={(e) => setVerifyPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRoleDialog(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateRoleMutation.isPending || !verifyPassword}
              >
                {updateRoleMutation.isPending ? "Verifying..." : "Confirm"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change password - Admin only, requires password verification */}
      <Dialog
        open={!!passwordDialog}
        onOpenChange={(open) => {
          if (!open) setPasswordDialog(null);
          setVerifyError("");
          setVerifyPassword("");
          setNewPassword("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Set a new password for <strong>{passwordDialog?.name}</strong>.
            Enter your password to verify, then the new password.
          </p>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {verifyError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {verifyError}
              </div>
            )}
            <div>
              <label htmlFor="change-password-verify" className="block text-sm font-medium text-slate-700 mb-1">
                Your Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="change-password-verify"
                  name="verifyPassword"
                  type="password"
                  value={verifyPassword}
                  onChange={(e) => setVerifyPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div>
              <label htmlFor="change-password-new" className="block text-sm font-medium text-slate-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="change-password-new"
                  name="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordDialog(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={changePasswordMutation.isPending || !verifyPassword || !newPassword}
              >
                {changePasswordMutation.isPending ? "Updating..." : "Update Password"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete user - confirmation only */}
      <Dialog
        open={!!deleteDialog}
        onOpenChange={(open) => {
          if (!open) setDeleteDialog(null);
          setVerifyError("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete <strong>{deleteDialog?.name}</strong>? This action cannot be undone.
          </p>
          <form onSubmit={handleDeleteUser} className="space-y-4">
            {verifyError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {verifyError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteDialog(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete User"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User */}
      <Dialog
        open={createUserDialog}
        onOpenChange={(open) => {
          if (!open) {
            setCreateUserDialog(false);
            setCreateError("");
            setCreateName("");
            setCreateEmail("");
            setCreatePassword("");
            setCreateRole("user");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Add a new user account. They can sign in with the email and password you set.
          </p>
          <form onSubmit={handleCreateUser} className="space-y-4">
            {createError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {createError}
              </div>
            )}
            <div>
              <label htmlFor="create-user-name" className="block text-sm font-medium text-slate-700 mb-1">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="create-user-name"
                  name="name"
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10"
                  required
                  autoComplete="name"
                />
              </div>
            </div>
            <div>
              <label htmlFor="create-user-email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="create-user-email"
                  name="email"
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10"
                  required
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <label htmlFor="create-user-password" className="block text-sm font-medium text-slate-700 mb-1">
                Password (8+ chars, upper, lower, number, symbol)
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="create-user-password"
                  name="password"
                  type="password"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div>
              <label htmlFor="create-user-role" className="block text-sm font-medium text-slate-700 mb-1">
                Role
              </label>
              <Select value={createRole} onValueChange={setCreateRole}>
                <SelectTrigger id="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateUserDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createUserMutation.isPending}
              >
                {createUserMutation.isPending ? "Creating..." : "Create User"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
